import { useNavigate, useLocation } from 'react-router-dom';
import {
  useState, memo, useRef, useEffect, useMemo,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import useGetVideosByAlertId from '../../../API/hooks/videos/useGetVideosByAlertId';
import usePostRequestVideo from '../../../API/hooks/alerts/usePostRequestVideo';
import S from './MultipleVideoStyles';
import NoVideoContent from './NoVideoContent/NoVideoContent';
import { Button } from '../../ui/Button/Button';
import Icon from '../../common/Icon/Icon';
import { IconKeys } from '../../common/Icon/IconKeys';
import AlertsCard from './AlertsCard/AlertsCard';
import { convertMsToKm } from '../../../utils/unitsUtils';
import SingleVideo from '../SingleVideo/SingleVideo';
import CircularProgress from '../../common/CircularProgress/CircularProgress';
import useGetFleetFeatures from '../../../API/hooks/useGetFleetFeatures';
import {
  AlertVideoListItem,
  findNewlyCompletedVideoIds,
  groupVideosByCamera,
  nextUploadPhaseByCamera,
  shouldShowAlertVideoPendingUi,
  shouldShowAlertVideoUploadUi,
} from './alertVideoUtils';

const TABS = {
  OUTWARD: 'outward',
  INWARD: 'inward',
  MULTIPLE: 'multiple',
};

const TABS_CONFIG = {
  [TABS.OUTWARD]: {
    title: 'Outward camera',
    cameraId: 1,
  },
  [TABS.INWARD]: {
    title: 'Inward camera',
    cameraId: 2,
  },
  [TABS.MULTIPLE]: {
    title: 'Multiple cameras',
    cameraId: 'all',
  },
};

const TABS_LIST = Object.values(TABS);

const INWARD_CAMERA_ID = TABS_CONFIG[TABS.INWARD].cameraId;

const VIDEO_STATUS_MAP: { [key: string]: string } = {
  pending: 'Requesting video',
  startedProcessing: 'Video processing started',
  failedToRequest: 'Video request failed',
  failedToUpload: 'Video upload failed',
  failedFinally: 'Video is not available on the device',
  failedAttemptsLimitExceeded: 'video could not be downloaded due to connection issue',
  failedDeviceRemovedFromFleet: 'Video unavailable - device was removed from the fleet',
};

const getCurrentVideo = (videos: any, currentTab: string): any => (
  currentTab === TABS.MULTIPLE
    ? videos && Object.values(videos)
      .filter((video: any) => video.url)
      .map((video: any) => ({
        _id: video._id,
        src: video.url,
        cameraId: video.cameraId,
        status: video.status,
        hasAudio: video.hasAudio,
      }))
    : videos && videos[TABS_CONFIG[currentTab]?.cameraId]
);

const normalizeAlertVideos = (data: unknown): AlertVideoListItem[] => {
  if (!data) return [];
  if (Array.isArray(data)) return data as AlertVideoListItem[];
  return Object.values(data as Record<string, AlertVideoListItem[]>).flat();
};

function MultipleVideo({
  fleetId, alertId, deviceId, driverPhotoUrl, info, active = TABS.OUTWARD, synchronized = true, deviceStatus,
}: {
  fleetId?: string,
  alertId?: string,
  deviceId?: string,
  driverPhotoUrl?: string | null,
  info?: any,
  active?: string,
  synchronized?: boolean,
  deviceStatus?: any,
}): JSX.Element {
  const [currentTab, setCurrentTab] = useState(active);
  const [currentTime, setCurrentTime] = useState(0);
  const [playingStatus, setPlayingStatus] = useState(false);
  const [lastTouchedCamera, setLastTouchedCamera] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const videoStatusRef = useRef<Record<string, string>>({});
  const { data, isLoading } = useGetVideosByAlertId(alertId as string, fleetId as string);
  const videos = useMemo(
    () => groupVideosByCamera(normalizeAlertVideos(data)),
    [data],
  );
  const navigate = useNavigate();
  const location = useLocation();
  const { videoRecording: videoRecordingEnabled } = useGetFleetFeatures();
  const hasDriverPhoto = !!driverPhotoUrl;
  // Unknown/unauthorized driver events carry a photo url even when the device never uploaded the
  // picture, so a failing url is tracked to render a message instead of a broken image.
  const [failedDriverPhotoUrl, setFailedDriverPhotoUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!hasDriverPhoto || !alertId) {
      return;
    }
    if (currentTab !== TABS.INWARD) {
      setCurrentTab(TABS.INWARD);
      navigate(`/alerts/${alertId}?camera=${TABS.INWARD}`, {
        replace: true,
        state: location.state,
      });
    }
  }, [alertId, currentTab, hasDriverPhoto, location.state, navigate]);

  // Store stable URLs to prevent player resets during refetching
  const stableUrlsRef = useRef<Record<string | number, string>>({});

  // Stable upload progress: animates from previous value, never jumps to 0 abruptly.
  // When the server stops sending uploadPercentage we keep the last known value
  // so CircularProgress stays mounted and its own RAF animation runs to completion.
  const [stableUploadProgress, setStableUploadProgress] = useState<number | null>(null);
  const [uploadPhaseByCamera, setUploadPhaseByCamera] = useState<Record<number, true>>({});
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep signed URLs fresh across polls; update when the API returns a new url.
  useEffect(() => {
    if (!videos) return;
    Object.entries(videos).forEach(([cameraId, videoData]) => {
      if (videoData?.url) {
        stableUrlsRef.current[cameraId] = videoData.url;
      }
    });
  }, [videos]);

  useEffect(() => {
    setUploadPhaseByCamera((previous) => nextUploadPhaseByCamera(videos, previous));
  }, [videos]);

  useEffect(() => {
    const alertVideos = normalizeAlertVideos(data);
    const newlyCompleted = findNewlyCompletedVideoIds(alertVideos, videoStatusRef.current);
    alertVideos.forEach((video) => {
      videoStatusRef.current[video._id] = video.status ?? '';
    });
    if (newlyCompleted.length > 0 && alertId && fleetId) {
      queryClient.invalidateQueries({ queryKey: ['alert', alertId, fleetId] });
    }
  }, [data, alertId, fleetId, queryClient]);

  const rawCurrentVideo = getCurrentVideo(videos, currentTab);
  const currentVideo = useMemo(() => {
    if (!rawCurrentVideo) return rawCurrentVideo;

    if (Array.isArray(rawCurrentVideo)) {
      return rawCurrentVideo.map((video: any) => {
        const stableUrl = stableUrlsRef.current[video.cameraId];
        if (stableUrl && stableUrl === video.src) return video;
        return {
          ...video,
          src: stableUrl || video.src,
        };
      });
    }

    const cameraId = TABS_CONFIG[currentTab]?.cameraId;
    const stableUrl = stableUrlsRef.current[cameraId];
    if (stableUrl && stableUrl === rawCurrentVideo.url) return rawCurrentVideo;
    return {
      ...rawCurrentVideo,
      url: stableUrl || rawCurrentVideo.url,
    };
  }, [rawCurrentVideo, currentTab]);

  // Track uploadPercentage: update stableUploadProgress when a real value arrives,
  // animate to 0 (by setting 0 and waiting for the 500ms animation) before hiding.
  const activeCameraId = Number(TABS_CONFIG[currentTab]?.cameraId);
  const uploadPhaseLocked = Boolean(uploadPhaseByCamera[activeCameraId]);
  const isUploading = currentVideo?.status === 'startedProcessing';
  const showUploadUi = shouldShowAlertVideoUploadUi(
    currentVideo,
    uploadPhaseLocked,
    stableUploadProgress,
  );
  const showPendingUi = shouldShowAlertVideoPendingUi(
    currentVideo,
    uploadPhaseLocked,
    stableUploadProgress,
  );
  const livePercentage: number | undefined = typeof currentVideo?.uploadPercentage === 'number'
    ? currentVideo.uploadPercentage
    : undefined;

  useEffect(() => {
    if (!currentVideo?.url) return;
    setStableUploadProgress(null);
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, [currentVideo?.url]);

  useEffect(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }

    if (!isUploading && !uploadPhaseLocked) {
      // Animate to 0 then hide after animation completes (500ms matches CircularProgress duration)
      if (stableUploadProgress !== null) {
        setStableUploadProgress(0);
        hideTimerRef.current = setTimeout(() => setStableUploadProgress(null), 550);
      }
      return;
    }

    if (!isUploading && uploadPhaseLocked && stableUploadProgress === null) {
      setStableUploadProgress(0);
      return;
    }

    if (livePercentage !== undefined) {
      setStableUploadProgress(livePercentage);
    } else if (stableUploadProgress === null) {
      // First time we see an uploading status without a percentage yet
      setStableUploadProgress(0);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUploading, livePercentage, uploadPhaseLocked]);

  useEffect(() => () => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
  }, []);

  const [requestedCameraIds, setRequestedCameraIds] = useState<Record<number, boolean>>({});
  const [requestError, setRequestError] = useState<Record<number, boolean>>({});

  const { mutate: postRequestVideo, isLoading: isRequestingVideo } = usePostRequestVideo(
    () => { },
    () => { },
  );

  const handleTabClick = (tab: string): void => {
    if (hasDriverPhoto && tab !== TABS.INWARD) {
      return;
    }
    navigate(`/alerts/${alertId}?camera=${tab}`, {
      replace: true,
      state: location.state,
    });
    setCurrentTab(tab);
  };

  const isTabDisabled = (tab: string): boolean => hasDriverPhoto && tab !== TABS.INWARD;

  const handleRequestVideo = (cameraIdOverride?: number): void => {
    const cameraId = cameraIdOverride ?? Number(TABS_CONFIG[currentTab]?.cameraId);
    setRequestedCameraIds((prev) => ({ ...prev, [cameraId]: true }));
    setRequestError((prev) => ({ ...prev, [cameraId]: false }));
    postRequestVideo({
      alertId: alertId as string,
      fleetId: fleetId as string,
      deviceId: deviceId as string,
      cameraIds: [cameraId],
    }, {
      onError: () => {
        setRequestedCameraIds((prev) => ({ ...prev, [cameraId]: false }));
        setRequestError((prev) => ({ ...prev, [cameraId]: true }));
      },
    });
  };

  const handleVideoOnTimeChange = (e: any): void => {
    setLastTouchedCamera(e.videoId);
    setCurrentTime(e.time);
    setPlayingStatus(e.playing);
  };

  const shouldShowDriverPhoto = (cameraId: number): boolean => (
    cameraId === INWARD_CAMERA_ID && !!driverPhotoUrl
  );

  const renderDriverPhoto = (): JSX.Element | null => (
    driverPhotoUrl
      ? (
        <div className="driver-photo-container">
          {failedDriverPhotoUrl === driverPhotoUrl
            ? <span className="placeholder-message">Picture not available</span>
            : (
              <img
                className="driver-photo"
                src={driverPhotoUrl}
                alt="Driver"
                onError={() => setFailedDriverPhotoUrl(driverPhotoUrl)}
              />
            )}
        </div>
      )
      : null
  );

  const renderMultipleCameraSlot = ({
    cameraId,
    stableUrl,
    video,
    failedLabel,
    deviceRemovedLabel,
    slotShowPendingUi,
    slotShowUploadUi,
    slotUploadProgress,
    status,
  }: {
    cameraId: number;
    stableUrl?: string;
    video?: AlertVideoListItem;
    failedLabel: string | null;
    deviceRemovedLabel: string | null;
    slotShowPendingUi: boolean;
    slotShowUploadUi: boolean;
    slotUploadProgress: number | null;
    status?: string;
  }): JSX.Element => {
    if (stableUrl) {
      return (
        <SingleVideo
          url={stableUrl}
          isFullSize={false}
          fillSlot
          videoId={video?._id}
          onTimeChange={handleVideoOnTimeChange}
          selectedTime={lastTouchedCamera !== video?._id ? currentTime : undefined}
          isPlaying={playingStatus}
          hasAudio={video?.hasAudio}
        />
      );
    }

    if (shouldShowDriverPhoto(cameraId) && !stableUrl) {
      return renderDriverPhoto() as JSX.Element;
    }

    return (
      <div className="video-placeholder">
        {failedLabel || deviceRemovedLabel || (slotShowPendingUi && (
          <span className="placeholder-message">Video Request Pending</span>
        )) || (slotShowUploadUi && slotUploadProgress !== null && (
          <CircularProgress progress={slotUploadProgress} labelText="Uploading video..." />
        )) || (slotShowUploadUi && (
          <span className="placeholder-message">Uploading video...</span>
        )) || (!video && videoRecordingEnabled && deviceId && !shouldShowDriverPhoto(cameraId) && requestedCameraIds[cameraId] && (
          <span className="placeholder-message">Video Requested</span>
        )) || (!video && videoRecordingEnabled && deviceId && !shouldShowDriverPhoto(cameraId) && (
          <Button onClick={() => handleRequestVideo(cameraId)} disabled={isRequestingVideo}>
            <Icon iconKey={IconKeys.ADD_VIDEO} />
            {isRequestingVideo ? 'Requesting...' : 'Request a video feed'}
          </Button>
        )) || (
          <span className="placeholder-message">{VIDEO_STATUS_MAP[status ?? ''] || VIDEO_STATUS_MAP.pending}</span>
        )}
        {requestError[cameraId] && (
          <span className="placeholder-message">Failed to request video, please try again</span>
        )}
      </div>
    );
  };

  return (
    <S.Wrapper>
      <div className="widgets-container">
        <ul className="nav-container">
          {TABS_LIST.map((tab) => {
            const disabled = isTabDisabled(tab);
            return (
              <li key={tab}>
                <button
                  type="button"
                  className={`${currentTab === tab ? 'nav-selected' : ''}${disabled ? ' nav-disabled' : ''}`}
                  onClick={() => handleTabClick(tab)}
                  id={tab}
                  disabled={disabled}
                  aria-disabled={disabled}
                >
                  {TABS_CONFIG[tab].title}
                </button>
              </li>
            );
          })}
        </ul>
        <div>
          <div>
            <div className={`video-player-wrapper ${currentTab === TABS.MULTIPLE ? 'multiple' : ''}`}>
              {!isLoading && hasDriverPhoto && renderDriverPhoto()}

              {isLoading && !hasDriverPhoto && (
                <div className="no-video">Loading...</div>
              )}

              {/* Single camera tabs */}
              {!isLoading && !hasDriverPhoto && currentTab !== TABS.MULTIPLE && (
                <>
                  {currentVideo?.url && (
                    <SingleVideo url={currentVideo.url} hasAudio={currentVideo.hasAudio} status={currentVideo.status} />
                  )}
                  {!currentVideo?.url && (
                    shouldShowDriverPhoto(Number(TABS_CONFIG[currentTab]?.cameraId))
                      ? renderDriverPhoto()
                      : (
                        <>
                          {['failedToRequest', 'failedToUpload', 'failedAttemptsLimitExceeded', 'failedFinally', 'failedDeviceRemovedFromFleet'].includes(currentVideo?.status) && !showUploadUi && !showPendingUi && (
                            <div className="no-video">{VIDEO_STATUS_MAP[currentVideo.status] || 'Video upload error'}</div>
                          )}
                          {showPendingUi && (
                            <div className="no-video"><span>Video Request Pending</span></div>
                          )}
                          {showUploadUi && (
                            <div className="no-video">
                              {stableUploadProgress !== null ? (
                                <CircularProgress progress={stableUploadProgress} labelText="Uploading video..." />
                              ) : (
                                <span>Uploading video...</span>
                              )}
                            </div>
                          )}
                          {!currentVideo?.status && !showUploadUi && stableUploadProgress === null && !deviceId && (
                            <div className="no-video">Video unavailable - device was removed from the fleet</div>
                          )}
                          {!currentVideo?.status && !showUploadUi && stableUploadProgress === null && deviceId && (
                            videoRecordingEnabled
                              ? (
                                <>
                                  {requestedCameraIds[Number(TABS_CONFIG[currentTab]?.cameraId)] ? (
                                    <div className="no-video"><span>Video Requested</span></div>
                                  ) : (
                                    <Button
                                      className="no-video"
                                      onClick={() => handleRequestVideo()}
                                      disabled={isRequestingVideo}
                                    >
                                      <Icon iconKey={IconKeys.ADD_VIDEO} />
                                      {isRequestingVideo ? 'Requesting...' : 'Request a video feed'}
                                    </Button>
                                  )}
                                  {requestError[Number(TABS_CONFIG[currentTab]?.cameraId)] && (
                                    <div className="no-video"><span>Failed to request video, please try again</span></div>
                                  )}
                                </>
                              )
                              : <NoVideoContent />
                          )}
                        </>
                      )
                  )}
                </>
              )}

              {/* Multiple cameras tab — always 2 fixed slots */}
              {!isLoading && !hasDriverPhoto && currentTab === TABS.MULTIPLE && ([
                TABS_CONFIG[TABS.OUTWARD].cameraId,
                TABS_CONFIG[TABS.INWARD].cameraId,
              ] as number[]).map((cameraId) => {
                const video = videos?.[cameraId];
                const liveUrl = video?.url;
                if (liveUrl && !stableUrlsRef.current[cameraId]) {
                  stableUrlsRef.current[cameraId] = liveUrl;
                }
                const stableUrl = stableUrlsRef.current[cameraId];

                const slotUploadPhaseLocked = Boolean(uploadPhaseByCamera[cameraId]);
                const slotShowUploadUi = shouldShowAlertVideoUploadUi(
                  video,
                  slotUploadPhaseLocked,
                  typeof video?.uploadPercentage === 'number' ? video.uploadPercentage : null,
                );
                const slotShowPendingUi = shouldShowAlertVideoPendingUi(
                  video,
                  slotUploadPhaseLocked,
                  typeof video?.uploadPercentage === 'number' ? video.uploadPercentage : null,
                );
                const slotUploadProgress = (() => {
                  if (typeof video?.uploadPercentage === 'number') {
                    return video.uploadPercentage;
                  }
                  if (slotUploadPhaseLocked) {
                    return 0;
                  }
                  return null;
                })();
                const status = video?.status;
                const FAILED_STATUSES = ['failedAttemptsLimitExceeded', 'failedFinally', 'failedDeviceRemovedFromFleet', 'failedToRequest', 'failedToUpload'];
                const failedLabel = status && FAILED_STATUSES.includes(status) ? VIDEO_STATUS_MAP[status] : null;
                const deviceRemovedLabel = !deviceId ? 'Video unavailable - device was removed from the fleet' : null;
                return (
                  <div key={`slot-${cameraId}`} className="video-slot">
                    {renderMultipleCameraSlot({
                      cameraId,
                      stableUrl,
                      video,
                      failedLabel,
                      deviceRemovedLabel,
                      slotShowPendingUi,
                      slotShowUploadUi,
                      slotUploadProgress,
                      status,
                    })}
                  </div>
                );
              })}
            </div>
            <div className="video-description">
              <AlertsCard
                data={{
                  name: 'Vehicle speed',
                  title: info.speedMs !== null ? `${convertMsToKm(info.speedMs)} km/h` : 'N/A',
                }}
              />
              {!synchronized && (
                <AlertsCard
                  data={{
                    name: 'Video time might be incorrect',
                    title: 'Synchronization issue!',
                    color: 'red',
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </S.Wrapper>
  );
}

export default memo(MultipleVideo);
